import React, { useCallback, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import { computeSummarizeTotal } from "../domain/computeSummarizeTotal";
import { TransactionDto } from "../index";
import { AmountRender, DateRender } from "../renders";
import { StyledSpinner } from "../styles";
import { StyledIcon, StyledTable } from "./styles";

const createMessage = <T extends Record<string, Record<string, MessageDescriptor>>>(messages: T) =>
  messages;

const messages = createMessage({
  operationType: {
    refund: {
      id: "reservationDetail.transactionList.operationType.refund",
      defaultMessage: "Refund",
    },
    charge: {
      id: "reservationDetail.transactionList.operationType.charge",
      defaultMessage: "Charge",
    },
    authorization: {
      id: "reservationDetail.transactionList.operationType.authorization",
      defaultMessage: "Authorization",
    },
    deposit: {
      id: "reservationDetail.transactionList.operationType.deposit",
      defaultMessage: "Deposit",
    },
  },
  transactionList: {
    title: {
      id: "reservationDetail.transactionList.title",
      defaultMessage: "Transaction List",
    },
    noRecordsFound: {
      id: "reservationDetail.transactionList.noRecordsFound",
      defaultMessage: "No records found",
    },
    bookingEngineCharge: {
      id: "reservationDetail.transactionList.bookingEngineCharge",
      defaultMessage: "Default charge",
    },
    bookingEngineRefund: {
      id: "reservationDetail.transactionList.bookingEngineRefund",
      defaultMessage: "Backoffice refund",
    },
    copyToClipboardTooltip: {
      id: "reservationDetail.transactionList.copyToClipboardTooltip",
      defaultMessage: "Copy transaction ID to clipboard",
    },
    tableHeader: {
      date: {
        id: "reservationDetail.transactionList.tableHeader.date",
        defaultMessage: "Date",
      },
      transactionId: {
        id: "reservationDetail.transactionList.tableHeader.transactionId",
        defaultMessage: "Transaction ID",
      },
      orderNumber: {
        id: "reservationDetail.transactionList.tableHeader.orderNumber",
        defaultMessage: "Order number",
      },
      total: {
        id: "reservationDetail.transactionList.tableHeader.total",
        defaultMessage: "Total (EUR)",
      },
      type: {
        id: "reservationDetail.transactionList.tableHeader.type",
        defaultMessage: "Type",
      },
      description: {
        id: "reservationDetail.transactionList.tableHeader.description",
        defaultMessage: "Description",
      },
      status: {
        id: "reservationDetail.transactionList.tableHeader.status",
        defaultMessage: "Status",
      },
      creditCardNumber: {
        id: "reservationDetail.transactionList.tableHeader.creditCardNumber",
        defaultMessage: "Card",
      },
      user: {
        id: "reservationDetail.transactionList.tableHeader.user",
        defaultMessage: "User",
      },
      totalSum: {
        id: "reservationDetail.transactionList.tableHeader.totalSum",
        defaultMessage: "Total Sum",
      },
    },
  },
  errors: {
    getTransactionsError: {
      id: "reservationDetail.transactionList.getTransactionsError",
      defaultMessage: "Error on getting transactions",
    },
  },
});

export { computeSummarizeTotal } from "../domain/computeSummarizeTotal";

export interface TransactionListProps {
  isLoading: boolean;
  data: TransactionDto[];
  paymentType?: string;
  summarizeTotal: number;
}

export function TransactionList(props: TransactionListProps) {
  const intl = useIntl();

  const transactions = useMemo(() => {
    return props.data.map(it => ({
      ...it,
      total: it.type === "REFUND" ? it.total * -1 : it.total,
    }));
  }, [props.data]);

  const { summarizeTotal } = props;

  const getSafeDescription = useCallback(
    (description?: string) => {
      switch (description) {
        case "BOOKING_ENGINE_CHARGE":
          return <FormattedMessage {...messages.transactionList.bookingEngineCharge} />;
        case "BOOKING_ENGINE_REFUND":
          return <FormattedMessage {...messages.transactionList.bookingEngineRefund} />;
        case "DEBIT":
        case "AUTHORIZATION":
        case "AUTHORISATION":
          return intl.formatMessage(messages.operationType.authorization);
        case "DEPOSIT":
          return intl.formatMessage(messages.operationType.deposit);
        default:
          return description;
      }
    },
    [intl]
  );

  const handleCopyTransactionIdToClipboard = useCallback((transactionId: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(transactionId).catch(e => {
        console.error("Error copying transaction IDs to clipboard", e);
      });
    } else {
      console.warn("Clipboard API not supported");
    }
  }, []);

  const canBeRefunded = useCallback((transaction: TransactionDto) => {
    return transaction?.type !== "REFUND" && transaction?.status === "OK";
  }, []);

  return (
    <div>
      <div className="border-bottom pb-2 mb-3 text-[21px]">
        <FormattedMessage {...messages.transactionList.title} />
        {props.isLoading && (
          <span>
            {" "}
            <StyledSpinner className="fa fa-spinner fa-spin" />
          </span>
        )}
      </div>
      <div className="mb-3">
        <StyledTable className="table table-striped table-borderless">
          <thead>
            <tr>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.date} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.transactionId} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.orderNumber} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.total} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.type} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.description} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.status} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.creditCardNumber} />
              </th>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.user} />
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions && transactions.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <FormattedMessage {...messages.transactionList.noRecordsFound} />
                </td>
              </tr>
            ) : (
              transactions.map((it: TransactionDto, index) => (
                <tr key={index}>
                  <td>
                    <DateRender date={it.date} />
                  </td>
                  <td>
                    <>
                      {it.id}
                      {canBeRefunded(it) && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="copy-clipboard-tooltip" {...props}>
                              <FormattedMessage
                                {...messages.transactionList.copyToClipboardTooltip}
                              />
                            </Tooltip>
                          }>
                          <StyledIcon
                            className="fa fa-clipboard ml-2 pointer"
                            role="button"
                            onClick={() => handleCopyTransactionIdToClipboard(it.id!)}
                          />
                        </OverlayTrigger>
                      )}
                    </>
                  </td>
                  <td>{it.orderNumber}</td>
                  <td>
                    <AmountRender amount={it.total} />
                  </td>
                  <td>{it.type}</td>
                  <td>{getSafeDescription(it.description)}</td>
                  <td>
                    <span>{it.status}</span>
                    {it.statusInfo && (
                      <span className="ml-2">
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip id="error-tooltip" {...props}>
                              {it.statusInfo}
                            </Tooltip>
                          }>
                          <StyledIcon className="fa fa-info-circle" />
                        </OverlayTrigger>
                      </span>
                    )}
                  </td>
                  <td>{it.creditCardNumber}</td>
                  <td>{it.user}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <th scope="col">
                <FormattedMessage {...messages.transactionList.tableHeader.totalSum} />
              </th>
              <th colSpan={2}></th>
              <th>
                <AmountRender amount={summarizeTotal} />
              </th>
              <th colSpan={5} />
            </tr>
          </tfoot>
        </StyledTable>
      </div>
    </div>
  );
}
